import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDate, toISODateString } from '../lib/utils';
import {
  Users, ShieldCheck, Package, AlertTriangle, ChefHat, UserCheck,
  ClipboardCheck, Plus, ArrowLeft, Trash2, Eye, CheckCircle2, XCircle,
  Calendar, Pencil, Send
} from 'lucide-react';

const DEFAULT_CONFIG = {
  sections: [
    { id: 'client', label: 'Client', enabled: true, fields: [
      { id: 'client_volume_estimation', label: 'Volume estimation', type: 'number', enabled: true },
      { id: 'client_volume_reel', label: 'Volume réel', type: 'number', enabled: true },
      { id: 'client_heures_rush', label: 'Heures de rush', type: 'text', enabled: true },
      { id: 'client_retours_positifs', label: 'Retours positifs', type: 'textarea', enabled: true },
      { id: 'client_retours_negatifs', label: 'Retours négatifs', type: 'textarea', enabled: true },
      { id: 'client_problemes', label: 'Problèmes rencontrés', type: 'textarea', enabled: true },
      { id: 'client_commandes_speciales', label: 'Commandes spéciales / Clients réguliers', type: 'textarea', enabled: true },
    ]},
    { id: 'menage', label: 'Ménage & Hygiène', enabled: true, fields: [
      { id: 'menage_etat_general', label: 'État général', type: 'select', enabled: true },
      { id: 'menage_zones', label: 'Zones nettoyées', type: 'zones', enabled: true },
      { id: 'menage_poubelles', label: 'Poubelles vidées', type: 'checkbox', enabled: true },
      { id: 'menage_produits_recharger', label: 'Produits à recharger', type: 'textarea', enabled: true },
    ]},
    { id: 'stock', label: 'Inventaire & Stock', enabled: true, fields: [
      { id: 'stock_ruptures', label: 'Ruptures ou quasi-ruptures', type: 'textarea', enabled: true },
      { id: 'stock_produits_critiques', label: 'Produits critiques pour le soir', type: 'textarea', enabled: true },
      { id: 'stock_preparations_restantes', label: 'Préparations restantes', type: 'textarea', enabled: true },
      { id: 'stock_dlc_surveiller', label: 'DLC à surveiller', type: 'textarea', enabled: true },
      { id: 'stock_commandes_passer', label: 'Commandes à passer', type: 'textarea', enabled: true },
    ]},
    { id: 'info', label: 'Informations importantes', enabled: true, fields: [
      { id: 'info_problemes_techniques', label: 'Problèmes techniques', type: 'textarea', enabled: true },
      { id: 'info_incident', label: 'Incident client ou équipe', type: 'textarea', enabled: true },
      { id: 'info_retard_livraison', label: 'Retard livraison fournisseur', type: 'textarea', enabled: true },
      { id: 'info_organisation_soir', label: 'Organisation soir', type: 'textarea', enabled: true },
    ]},
    { id: 'production', label: 'Production / Préparation', enabled: true, fields: [
      { id: 'production_prepare', label: 'Ce qui a été préparé', type: 'textarea', enabled: true },
      { id: 'production_reste_produire', label: 'Reste à produire', type: 'textarea', enabled: true },
      { id: 'production_priorite', label: 'Produits à lancer en priorité', type: 'textarea', enabled: true },
    ]},
    { id: 'equipe', label: 'Équipe', enabled: true, fields: [
      { id: 'equipe_presents', label: 'Qui était présent', type: 'textarea', enabled: true },
      { id: 'equipe_difficultes', label: 'Difficultés rencontrées', type: 'textarea', enabled: true },
      { id: 'equipe_consignes_soir', label: 'Consignes pour le soir', type: 'textarea', enabled: true },
    ]},
  ],
  checklist: [
    { id: 'checklist_menage', label: 'Ménage OK', enabled: true },
    { id: 'checklist_stock', label: 'Stock OK', enabled: true },
    { id: 'checklist_prepa', label: 'Prépa OK', enabled: true },
    { id: 'checklist_brief', label: 'Brief fait', enabled: true },
  ],
};

const EMPTY_FORM = {
  date: toISODateString(new Date()),
  service: 'midi',
  client_volume_estimation: 0,
  client_volume_reel: 0,
  client_heures_rush: '',
  client_retours_positifs: '',
  client_retours_negatifs: '',
  client_problemes: '',
  client_commandes_speciales: '',
  menage_etat_general: 'ras',
  menage_zones: { cuisine: false, caisse: false, salle: false, exterieur: false },
  menage_poubelles: false,
  menage_produits_recharger: '',
  stock_ruptures: '',
  stock_produits_critiques: '',
  stock_preparations_restantes: '',
  stock_dlc_surveiller: '',
  stock_commandes_passer: '',
  info_problemes_techniques: '',
  info_incident: '',
  info_retard_livraison: '',
  info_organisation_soir: '',
  production_prepare: '',
  production_reste_produire: '',
  production_priorite: '',
  equipe_presents: '',
  equipe_difficultes: '',
  equipe_consignes_soir: '',
  checklist_menage: false,
  checklist_stock: false,
  checklist_prepa: false,
  checklist_brief: false,
  destinataires: [],
};

function Section({ icon: Icon, title, color, children }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function CheckItem({ label, checked, onChange, testId }) {
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-muted/30 border-border hover:bg-muted/60'}`}
      onClick={() => onChange(!checked)}
      data-testid={testId}
    >
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span className={`text-sm font-medium ${checked ? 'text-green-700 dark:text-green-400' : ''}`}>{label}</span>
      {checked ? <CheckCircle2 className="w-4 h-4 text-green-600 ml-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground/40 ml-auto" />}
    </div>
  );
}

function ZoneCheck({ label, checked, onChange }) {
  return (
    <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${checked ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' : 'border-border hover:bg-muted/40'}`}>
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

// Destinataires selector
function DestinatairesSelector({ employes, selected, onChange }) {
  const toggleEmployee = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(d => d !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {employes.map(emp => {
          const isSelected = selected.includes(emp.id);
          return (
            <button
              key={emp.id}
              type="button"
              onClick={() => toggleEmployee(emp.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-all ${
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-muted/40 border-border hover:bg-muted/80 text-foreground'
              }`}
              data-testid={`dest-${emp.id}`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isSelected ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
                {emp.prenom?.[0] || '?'}
              </div>
              {emp.prenom} {emp.nom || ''}
              {isSelected && <CheckCircle2 className="w-3.5 h-3.5" />}
            </button>
          );
        })}
      </div>
      {employes.length === 0 && <p className="text-sm text-muted-foreground">Aucun employé enregistré dans le module RH</p>}
    </div>
  );
}

// The form fields - dynamically built from config
function TransmissionFormFields({ form, updateField, updateZone, employes, config }) {
  const sections = config?.sections || [];
  const checklistItems = config?.checklist || [];
  const enabledChecks = checklistItems.filter(c => c.enabled);
  const checklistCount = enabledChecks.filter(c => form[c.id]).length;

  const sectionIcons = { client: Users, menage: ShieldCheck, stock: Package, info: AlertTriangle, production: ChefHat, equipe: UserCheck };
  const sectionColors = { client: 'bg-blue-500', menage: 'bg-emerald-500', stock: 'bg-orange-500', info: 'bg-red-500', production: 'bg-purple-500', equipe: 'bg-indigo-500' };

  const renderField = (field) => {
    if (!field.enabled) return null;
    const key = field.id;
    if (field.type === 'number') {
      return (
        <div key={key} className="space-y-1.5">
          <Label className="text-xs">{field.label}</Label>
          <Input type="number" value={form[key] || 0} onChange={e => updateField(key, parseInt(e.target.value) || 0)} data-testid={key} />
        </div>
      );
    }
    if (field.type === 'text') {
      return (
        <div key={key} className="space-y-1.5">
          <Label className="text-xs">{field.label}</Label>
          <Input value={form[key] || ''} onChange={e => updateField(key, e.target.value)} data-testid={key} />
        </div>
      );
    }
    if (field.type === 'select' && key === 'menage_etat_general') {
      return (
        <div key={key} className="space-y-1.5">
          <Label className="text-xs">{field.label}</Label>
          <Select value={form.menage_etat_general || 'ras'} onValueChange={v => updateField('menage_etat_general', v)}>
            <SelectTrigger data-testid="menage-etat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ras">RAS (Rien à signaler)</SelectItem>
              <SelectItem value="a_reprendre">A reprendre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (field.type === 'zones') {
      return (
        <div key={key} className="space-y-1.5">
          <Label className="text-xs">{field.label}</Label>
          <div className="grid grid-cols-2 gap-2">
            {['cuisine', 'caisse', 'salle', 'exterieur'].map(z => (
              <ZoneCheck key={z} label={z.charAt(0).toUpperCase() + z.slice(1)} checked={form.menage_zones?.[z] || false} onChange={v => updateZone(z, v)} />
            ))}
          </div>
        </div>
      );
    }
    if (field.type === 'checkbox') {
      return (
        <div key={key} className="flex items-center gap-3">
          <Checkbox checked={form[key] || false} onCheckedChange={v => updateField(key, v)} data-testid={key} />
          <Label className="text-sm cursor-pointer">{field.label}</Label>
        </div>
      );
    }
    // default: textarea
    return (
      <div key={key} className="space-y-1.5">
        <Label className="text-xs">{field.label}</Label>
        <Textarea value={form[key] || ''} onChange={e => updateField(key, e.target.value)} rows={2} data-testid={key} />
      </div>
    );
  };

  return (
    <>
      {/* Date & Service */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={e => updateField('date', e.target.value)} className="w-[180px]" data-testid="form-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Service</Label>
              <Select value={form.service} onValueChange={v => updateField('service', v)}>
                <SelectTrigger className="w-[140px]" data-testid="form-service"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="midi">Midi</SelectItem>
                  <SelectItem value="soir">Soir</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Badge variant="outline" className="text-sm gap-1">
                <ClipboardCheck className="w-3.5 h-3.5" /> Checklist: {checklistCount}/{enabledChecks.length}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Destinataires */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-500">
              <Send className="w-4 h-4 text-white" />
            </div>
            Destinataires du rapport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DestinatairesSelector employes={employes} selected={form.destinataires || []} onChange={v => updateField('destinataires', v)} />
        </CardContent>
      </Card>

      {/* Checklist rapide */}
      {enabledChecks.length > 0 && (
        <Card className="border-2 border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardCheck className="w-5 h-5" /> Checklist rapide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-3 ${enabledChecks.length <= 4 ? `grid-cols-2 md:grid-cols-${enabledChecks.length}` : 'grid-cols-2 md:grid-cols-4'}`}>
              {enabledChecks.map(c => (
                <CheckItem key={c.id} label={c.label} checked={form[c.id] || false} onChange={v => updateField(c.id, v)} testId={c.id} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sections.filter(s => s.enabled).map(section => {
          const Icon = sectionIcons[section.id] || Package;
          const color = sectionColors[section.id] || 'bg-gray-500';
          const enabledFields = section.fields.filter(f => f.enabled);
          if (enabledFields.length === 0) return null;

          const hasNumberPair = enabledFields.length >= 2 && enabledFields[0].type === 'number' && enabledFields[1].type === 'number';

          return (
            <Section key={section.id} icon={Icon} title={section.label} color={color}>
              {hasNumberPair && (
                <div className="grid grid-cols-2 gap-4">
                  {renderField(enabledFields[0])}
                  {renderField(enabledFields[1])}
                </div>
              )}
              {enabledFields.slice(hasNumberPair ? 2 : 0).map(field => renderField(field))}
            </Section>
          );
        })}
      </div>
    </>
  );
}


export function TransmissionPage() {
  const { api } = useAuth();
  const [view, setView] = useState('list');
  const [transmissions, setTransmissions] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editId, setEditId] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // edit mode in detail view
  const [transmissionConfig, setTransmissionConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => { fetchList(); fetchEmployes(); fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/settings/transmission-config');
      setTransmissionConfig(res.data);
    } catch {
      // Fallback default config
      setTransmissionConfig(DEFAULT_CONFIG);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/transmissions');
      setTransmissions(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const fetchEmployes = async () => {
    try {
      const res = await api.get('/transmissions/employes');
      setEmployes(res.data);
    } catch { /* silently fail */ }
  };

  const getEmployeName = (id) => {
    const emp = employes.find(e => e.id === id);
    return emp ? `${emp.prenom} ${emp.nom || ''}`.trim() : id;
  };

  const openNew = () => {
    setForm({ ...EMPTY_FORM, date: toISODateString(new Date()) });
    setEditId(null);
    setIsEditing(false);
    setView('form');
  };

  const openDetail = (item) => {
    setDetailItem(item);
    setForm({ ...EMPTY_FORM, ...item });
    setIsEditing(false);
    setView('detail');
  };

  const startEditing = () => {
    setForm({ ...EMPTY_FORM, ...detailItem });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm({ ...EMPTY_FORM, ...detailItem });
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    try {
      if (editId || (isEditing && detailItem?.id)) {
        const id = editId || detailItem.id;
        await api.put(`/transmissions/${id}`, form);
        toast.success('Transmission mise à jour');
        // refresh detail
        const res = await api.get(`/transmissions/${id}`);
        setDetailItem(res.data);
        setIsEditing(false);
      } else {
        await api.post('/transmissions', form);
        toast.success('Transmission enregistrée');
        setView('list');
      }
      fetchList();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/transmissions/${id}`);
      toast.success('Supprimé');
      fetchList();
      if (view === 'detail') setView('list');
    } catch { toast.error('Erreur'); }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateZone = (zone, val) => setForm(prev => ({ ...prev, menage_zones: { ...prev.menage_zones, [zone]: val } }));

  // ===== LIST VIEW =====
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="module-header">
          <div>
            <h1 className="module-title">Transmission de Service</h1>
            <p className="text-muted-foreground mt-1">Passation entre les services midi et soir</p>
          </div>
          <Button className="gap-2" onClick={openNew} data-testid="new-transmission-btn">
            <Plus className="w-4 h-4" /> Nouvelle transmission
          </Button>
        </div>

        {transmissions.length === 0 && !loading ? (
          <Card>
            <CardContent className="py-16 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">Aucune transmission enregistrée</p>
              <Button className="mt-4 gap-2" onClick={openNew}><Plus className="w-4 h-4" /> Créer la première</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {transmissions.map(t => {
              const checks = [t.checklist_menage, t.checklist_stock, t.checklist_prepa, t.checklist_brief].filter(Boolean).length;
              const destNames = (t.destinataires || []).map(id => getEmployeName(id));
              return (
                <Card key={t.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`transmission-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0" onClick={() => openDetail(t)}>
                        <div className="flex flex-col items-center flex-shrink-0">
                          <Calendar className="w-4 h-4 text-muted-foreground mb-1" />
                          <span className="text-sm font-semibold">{formatDate(t.date)}</span>
                        </div>
                        <Separator orientation="vertical" className="h-10" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={t.service === 'midi' ? 'default' : 'secondary'}>{t.service === 'midi' ? 'Midi' : 'Soir'}</Badge>
                            <span className="text-sm text-muted-foreground">par {t.auteur}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span>Clients: {t.client_volume_reel || '—'}</span>
                            <span>Checklist: {checks}/4</span>
                            {t.info_problemes_techniques && <Badge variant="outline" className="text-amber-600 border-amber-300">Prob. technique</Badge>}
                            {t.stock_ruptures && <Badge variant="outline" className="text-red-600 border-red-300">Ruptures</Badge>}
                            {destNames.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Send className="w-3 h-3" /> {destNames.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex gap-1">
                          {[t.checklist_menage, t.checklist_stock, t.checklist_prepa, t.checklist_brief].map((c, i) => (
                            <div key={i} className={`w-2.5 h-2.5 rounded-full ${c ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                          ))}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => openDetail(t)} data-testid={`view-${t.id}`}><Eye className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive" data-testid={`del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Supprimer cette transmission ?</AlertDialogTitle><AlertDialogDescription>Transmission du {formatDate(t.date)} ({t.service})</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(t.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ===== DETAIL VIEW (with inline edit) =====
  if (view === 'detail' && detailItem) {
    const t = isEditing ? form : detailItem;
    const checks = [t.checklist_menage, t.checklist_stock, t.checklist_prepa, t.checklist_brief];
    const checkLabels = ['Ménage', 'Stock', 'Prépa', 'Brief'];

    // Read-only detail field
    const DetailField = ({ label, value }) => {
      if (!value) return null;
      return (
        <div className="py-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
          <p className="text-sm whitespace-pre-wrap">{value}</p>
        </div>
      );
    };

    // If editing, show full form
    if (isEditing) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={cancelEditing} data-testid="cancel-edit-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Annuler les modifications</Button>
            <div className="flex-1">
              <h1 className="module-title">Modifier la Transmission</h1>
              <span className="text-sm text-muted-foreground">{formatDate(detailItem.date)} — {detailItem.service === 'midi' ? 'Midi' : 'Soir'}</span>
            </div>
          </div>
          <TransmissionFormFields form={form} updateField={updateField} updateZone={updateZone} employes={employes} config={transmissionConfig} />
          <div className="flex justify-end gap-3 pb-8">
            <Button variant="outline" onClick={cancelEditing}>Annuler</Button>
            <Button onClick={handleSubmit} className="gap-2 px-8" data-testid="save-edit-btn">
              <ClipboardCheck className="w-4 h-4" /> Enregistrer les modifications
            </Button>
          </div>
        </div>
      );
    }

    // Read-only detail
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setView('list')} data-testid="back-to-list"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
          <div className="flex-1">
            <h1 className="module-title">Transmission du {formatDate(t.date)}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={t.service === 'midi' ? 'default' : 'secondary'}>{t.service === 'midi' ? 'Service Midi' : 'Service Soir'}</Badge>
              <span className="text-sm text-muted-foreground">par {t.auteur}</span>
            </div>
          </div>
          <Button variant="outline" className="gap-2" onClick={startEditing} data-testid="edit-detail-btn">
            <Pencil className="w-4 h-4" /> Modifier
          </Button>
        </div>

        {/* Destinataires */}
        {(t.destinataires || []).length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Send className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-medium">Destinataires</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(t.destinataires || []).map(id => (
                  <Badge key={id} variant="secondary">{getEmployeName(id)}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Checklist summary */}
        <div className="grid grid-cols-4 gap-3">
          {checks.map((c, i) => (
            <div key={i} className={`flex items-center gap-2 p-3 rounded-lg border ${c ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'}`}>
              {c ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-400" />}
              <span className={`text-sm font-medium ${c ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{checkLabels[i]}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section icon={Users} title="Client" color="bg-blue-500">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-muted-foreground">Volume estimation</p><p className="text-lg font-bold">{t.client_volume_estimation || '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Volume réel</p><p className="text-lg font-bold">{t.client_volume_reel || '—'}</p></div>
            </div>
            <DetailField label="Heures de rush" value={t.client_heures_rush} />
            <DetailField label="Retours positifs" value={t.client_retours_positifs} />
            <DetailField label="Retours négatifs" value={t.client_retours_negatifs} />
            <DetailField label="Problèmes rencontrés" value={t.client_problemes} />
            <DetailField label="Commandes spéciales / Clients réguliers" value={t.client_commandes_speciales} />
          </Section>

          <Section icon={ShieldCheck} title="Ménage & Hygiène" color="bg-emerald-500">
            <div className="flex items-center gap-2">
              <span className="text-sm">État général:</span>
              <Badge variant={t.menage_etat_general === 'ras' ? 'default' : 'destructive'}>
                {t.menage_etat_general === 'ras' ? 'RAS' : 'A reprendre'}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(t.menage_zones || {}).map(([zone, done]) => (
                <Badge key={zone} variant={done ? 'default' : 'outline'} className={done ? 'bg-green-600' : ''}>
                  {zone.charAt(0).toUpperCase() + zone.slice(1)} {done ? '✓' : '✗'}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">Poubelles vidées:</span>
              <Badge variant={t.menage_poubelles ? 'default' : 'outline'}>{t.menage_poubelles ? 'Oui' : 'Non'}</Badge>
            </div>
            <DetailField label="Produits à recharger" value={t.menage_produits_recharger} />
          </Section>

          <Section icon={Package} title="Inventaire & Stock" color="bg-orange-500">
            <DetailField label="Ruptures ou quasi-ruptures" value={t.stock_ruptures} />
            <DetailField label="Produits critiques pour le soir" value={t.stock_produits_critiques} />
            <DetailField label="Préparations restantes" value={t.stock_preparations_restantes} />
            <DetailField label="DLC à surveiller" value={t.stock_dlc_surveiller} />
            <DetailField label="Commandes à passer" value={t.stock_commandes_passer} />
          </Section>

          <Section icon={AlertTriangle} title="Informations importantes" color="bg-red-500">
            <DetailField label="Problèmes techniques" value={t.info_problemes_techniques} />
            <DetailField label="Incident client ou équipe" value={t.info_incident} />
            <DetailField label="Retard livraison fournisseur" value={t.info_retard_livraison} />
            <DetailField label="Organisation soir" value={t.info_organisation_soir} />
          </Section>

          <Section icon={ChefHat} title="Production / Préparation" color="bg-purple-500">
            <DetailField label="Ce qui a été préparé" value={t.production_prepare} />
            <DetailField label="Reste à produire" value={t.production_reste_produire} />
            <DetailField label="Priorités" value={t.production_priorite} />
          </Section>

          <Section icon={UserCheck} title="Équipe" color="bg-indigo-500">
            <DetailField label="Présents" value={t.equipe_presents} />
            <DetailField label="Difficultés" value={t.equipe_difficultes} />
            <DetailField label="Consignes pour le soir" value={t.equipe_consignes_soir} />
          </Section>
        </div>
      </div>
    );
  }

  // ===== NEW FORM VIEW =====
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => setView('list')} data-testid="back-to-list-form"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
        <div className="flex-1">
          <h1 className="module-title">Nouvelle Transmission</h1>
        </div>
      </div>

      <TransmissionFormFields form={form} updateField={updateField} updateZone={updateZone} employes={employes} config={transmissionConfig} />

      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => setView('list')} data-testid="cancel-btn">Annuler</Button>
        <Button onClick={handleSubmit} className="gap-2 px-8" data-testid="submit-transmission">
          <ClipboardCheck className="w-4 h-4" /> Enregistrer la transmission
        </Button>
      </div>
    </div>
  );
}
